import { useEffect, useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../module/AuthContext';
import { toggleAuthModal } from '../../redux/authentication/action';
import { manageOffsiteCampaigns } from '../../redux/buzzbridge/actions';
import { gettingManagedCampaignsSelector, managedCampaignsSelector } from '../../redux/buzzbridge/selectors';
import type { RootState } from '../../redux/root-reducer';
import type { IInfluencer } from '../../redux/buzzbridge/types';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { BRAND_LUXE_PRIMARY } from '../../pages/BrandDashboard/brandDashboardTokens';
import { BrandDashboardInviteEmptyHint } from './BrandDashboardInviteEmptyHint';
import { InfluencerInviteToCampaignModal } from './InfluencerInviteToCampaignModal';

export interface PublicCreatorCollabBarProps {
    influencer: IInfluencer;
}

function collabFirstName(influencer: IInfluencer): string {
    const full = influencer.name?.trim();
    if (full) return full.split(/\s+/)[0] ?? full;
    return 'creator';
}

function isOwnInfluencerProfile(influencer: IInfluencer, userId?: string): boolean {
    if (!userId) return false;
    const cb = influencer.created_by;
    const creatorId = typeof cb === 'object' ? cb._id : cb;
    return Boolean(creatorId && creatorId === userId);
}

export function PublicCreatorCollabBar({ influencer }: PublicCreatorCollabBarProps) {
    const dispatch = useDispatch();
    const { isSignedIn, user } = useAuth();
    const token: string = useSelector((s: RootState) => s.authentication.token);
    const campaigns = useSelector(managedCampaignsSelector);
    const loadingCampaigns = useSelector(gettingManagedCampaignsSelector);
    const [inviteOpen, setInviteOpen] = useState(false);

    const ownProfile = useMemo(() => isOwnInfluencerProfile(influencer, user?._id), [influencer, user?._id]);

    useEffect(() => {
        if (inviteOpen && isSignedIn) {
            dispatch(manageOffsiteCampaigns({ sort: '-createdAt' }));
        }
    }, [inviteOpen, isSignedIn, dispatch]);

    if (ownProfile) return null;

    const firstName = collabFirstName(influencer);

    const handleCollabClick = () => {
        if (!isSignedIn) {
            dispatch(toggleAuthModal(true));
            return;
        }
        setInviteOpen(true);
    };

    return (
        <>
            <Box
                sx={{
                    position: 'fixed',
                    bottom: { xs: 24, md: 32 },
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 50,
                    width: '100%',
                    maxWidth: 448,
                    px: 3,
                    pointerEvents: 'none',
                }}>
                <Button
                    fullWidth
                    onClick={handleCollabClick}
                    endIcon={<ArrowForwardIcon sx={{ fontWeight: 700 }} />}
                    sx={{
                        pointerEvents: 'auto',
                        py: 2.25,
                        borderRadius: 999,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        fontSize: { xs: 14, sm: 16 },
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontWeight: 800,
                        color: '#392700',
                        background: `linear-gradient(to top right, ${BRAND_LUXE_PRIMARY}, #feb700)`,
                        boxShadow: '0 10px 40px rgba(255,201,101,0.35)',
                        '&:hover': {
                            transform: 'scale(1.03)',
                            boxShadow: '0 12px 48px rgba(255,201,101,0.45)',
                        },
                        transition: 'transform 0.2s, box-shadow 0.2s',
                    }}>
                    Collab with {firstName}
                </Button>
            </Box>

            <InfluencerInviteToCampaignModal
                open={inviteOpen}
                target={influencer}
                campaigns={campaigns}
                loadingCampaigns={loadingCampaigns}
                token={token}
                onClose={() => setInviteOpen(false)}
                title="Invite to collab"
                emptyMessage={<BrandDashboardInviteEmptyHint onBeforeSwitch={() => setInviteOpen(false)} />}
            />
        </>
    );
}

/** Bottom spacer so fixed CTA does not cover portfolio content */
export const PUBLIC_CREATOR_COLLAB_BAR_SPACER = { xs: 14, md: 16 };
